<template>
  <BaseDialog
    title="後台人工操作"
    width="610px"
    :btn1Loading="loading"
    @close="$emit('close')"
    @confirm="submit"
  >
    <BaseElForm ref="formRef" :model="formData" :rules="rules" label-position="top">
      <BaseElFormItem label="操作類型" prop="actionType">
        <BaseElSelect v-model="formData.actionType" placeholder="請選擇操作類型" class="w-full">
          <BaseElSelectOption label="後台作廢" value="userOrderVoid" />
          <BaseElSelectOption label="後台退貨" value="userOrderRefund" />
        </BaseElSelect>
      </BaseElFormItem>

      <BaseElFormItem v-if="formData.actionType === 'userOrderRefund'" label="選取退貨商品" prop="selectedItems">
        <BaseTable :data="refundItems" height="300px" @selection-change="handleSelectionChange">
          <BaseElTableColumn type="selection" width="55" align="center" />
          <BaseElTableColumn label="商品名稱" prop="name" align="center" />
          <BaseElTableColumn label="商品 ID" prop="itemNo" align="center" />
          <BaseElTableColumn label="單價" prop="price" align="center" />
          <BaseElTableColumn label="購買數量" prop="originalQuantity" align="center" />
          <BaseElTableColumn label="退貨數量" align="center" width="150">
            <template slot-scope="scope">
              <ElInputWrapper>
                <BaseElInputNumber
                  v-model="scope.row.quantity"
                  :min="1"
                  :max="scope.row.originalQuantity"
                  size="small"
                  style="width: 100%"
                />
              </ElInputWrapper>
            </template>
          </BaseElTableColumn>
        </BaseTable>
      </BaseElFormItem>

      <BaseElFormItem label="異動原因" prop="reason">
        <BaseElInput
          v-model="formData.reason"
          placeholder="請輸入異動原因"
        />
      </BaseElFormItem>
    </BaseElForm>

    <GrayBlockContainer v-if="hasReward && formData.actionType" class="mt-4 flex items-start gap-2">
      <MaterialIcon :size="24" class="text-primary-100 mt-0.5 flex-shrink-0">info_outline</MaterialIcon>
      <ol class="list-decimal pl-4 m-0 text-sm leading-relaxed text-gray-80">
        <li>操作後台{{ formData.actionType === 'userOrderVoid' ? '作廢' : '退貨' }}後，已發放的事件行銷獎勵系統將自動執行獎勵回收。</li>
        <li>
          事件行銷獎勵項目回收規則說明如下：
          <ul class="list-disc pl-4 m-0">
            <li>點數/回饋金：自動扣減已發放的數量，如<span class="text-primary-100">會員餘額不足將被扣至負數</span>。</li>
            <li>票券/使用券：如已<span class="text-primary-100">自動核銷將無法回收</span>。</li>
            <li>堂票：無論是否已使用，<span class="text-primary-100">將作廢整份堂票</span>，<span class="text-primary-100">使用完畢的堂票無法回收</span>。</li>
            <li>圖文訊息：已發送的訊息無法收回。</li>
          </ul>
        </li>
      </ol>
    </GrayBlockContainer>

    <GrayBlockContainer v-if="!formData.actionType" class="mt-4 flex items-start gap-2">
      <MaterialIcon :size="24" class="text-primary-100 mt-0.5 flex-shrink-0">info_outline</MaterialIcon>
      <ol class="list-decimal pl-4 m-0 text-sub leading-relaxed text-gray-80">
        <li>
          此操作會
          <span class="text-primary-100">更動訂單實際狀態，影響發放獎勵回收</span>
          ，如僅需標記訂單是否作廢，請返回上一頁使用「<span class="text-primary-100">手動標記</span>」功能。
        </li>
        <li>此人工操作後台作廢/退貨後，<span class="text-primary-100">訂單狀態不會同步至 POS 端</span>，如需變更 POS 端消費紀錄請至 POS 端操作調整。</li>
      </ol>
    </GrayBlockContainer>

    <BaseDialog
      v-if="showConfirmModal"
      title="提醒"
      width="400px"
      :btn1Loading="loading"
      @close="showConfirmModal = false"
      @confirm="executeSubmit"
    >
      <div>
        <p>訂單作廢後無法復原，請再次確認是否作廢？</p>
        <p class="text-danger mt-2">*訂單狀態不會同步至 POS 端</p>
      </div>
    </BaseDialog>
  </BaseDialog>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BaseTable from '@/components/BaseTable.vue'
import { VoidOrRefundMemberExternalTransaction } from '@/api/memberExternalTransaction'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import { get } from 'lodash'
import ElInputWrapper from '../ElInputWrapper.vue'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import MaterialIcon from '@/components/MaterialIcon.vue'

export default defineComponent({
  name: 'ExternalTransactionVoidModal',
  components: { BaseDialog, BaseTable, ElInputWrapper, GrayBlockContainer, MaterialIcon },
  props: {
    record: { type: Object, required: true },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formData, formRef, initFormData, checkForm } = useBaseForm()

    const refundItems = ref([])
    const showConfirmModal = ref(false)
    const loading = ref(false)

    const rules = {
      actionType: [{ required: true, message: '請選擇操作類型', trigger: 'change' }],
      selectedItems: [{ required: true, message: '請選擇退貨商品', trigger: 'change', type: 'array' }],
    }

    const hasReward = computed(() => {
      return get(props.record, 'eventPlaybookAwardRecords', []).length > 0
    })

    const initRefundItems = () => {
      refundItems.value = (props.record.items || []).map(item => ({
        ...item,
        originalQuantity: item.quantity,
        quantity: item.quantity,
      }))
    }

    onMounted(() => {
      initFormData({
        actionType: '',
        reason: '',
        selectedItems: [],
      })
      initRefundItems()
    })

    const handleSelectionChange = (val) => {
      formData.selectedItems = val
    }

    const submit = async () => {
      const valid = await checkForm(formRef.value)
      if (valid) {
        if (formData.actionType === 'userOrderRefund' && formData.selectedItems.length === 0) {
          window.$message.warning('請選擇退貨商品')
          return
        }
        showConfirmModal.value = true
      }
    }

    const executeSubmit = async () => {
      loading.value = true
      try {
        const payload = {
          shopId: shopId.value,
          id: props.record.id,
          type: formData.actionType,
          reason: formData.reason || undefined,
        }

        if (formData.actionType === 'userOrderRefund') {
          payload.items = formData.selectedItems.map(item => ({
            itemNo: item.itemNo,
            name: item.name,
            price: item.price,
            quantity: -item.quantity,
          }))
        }

        const [, err] = await VoidOrRefundMemberExternalTransaction(payload)
        if (err) {
          window.$message.error(err)
          return
        }

        window.$message.success('操作成功')
        emit('refresh')
        emit('close')
      } finally {
        loading.value = false
        showConfirmModal.value = false
      }
    }

    return {
      formData,
      formRef,
      refundItems,
      hasReward,
      showConfirmModal,
      loading,
      rules,
      handleSelectionChange,
      submit,
      executeSubmit,
    }
  },
})
</script>
